import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Zap, Play, Trash2, Power, Clock, AlertCircle, ChevronDown, Edit3, X, Loader2, Bot, Settings2, Webhook, Check, Send, Plug, Copy, History } from 'lucide-react';
import { IconInfo, IconPlus, IconSave, IconChat, IconX } from '../ui-icons';
import { pipelinesApi, automationGraphApi, automationAssistantApi, type PipelineInstance, type PipelineRun, type PipelinePatchInput, type PipelineCreateInput, type AutomationGraph, type AutomationGraphNode, type PipelineResponseTarget, type AutomationAssistantMessage, type PipelineVersion } from '../../api/pipelinesApi';
import { agentsApi } from '../../api/agentsApi';
import { haptic } from '../../telegram';
import type { Agent } from '../../types/contracts';
import { ConfirmSheet } from '../ConfirmSheet';

const TRIGGER_TYPES = [
  { value: 'inbound_webhook' as const, label: 'Вебхук (входящий)' },
  { value: 'schedule' as const, label: 'По расписанию' },
];
const RESPONSE_TARGETS: { value: PipelineResponseTarget; label: string }[] = [
  { value: 'telegram', label: 'Telegram' },
  { value: 'outbound_webhook', label: 'Вебхук наружу' },
  { value: 'both', label: 'Telegram + вебхук' },
  { value: 'none', label: 'Не отправлять' },
];
const RESP_LABEL: Record<string, string> = {
  telegram: 'Ответ в Telegram', outbound_webhook: 'Вебхук наружу', both: 'Telegram + вебхук', none: 'Не отправлять',
};
const RUN_LABEL: Record<string, string> = {
  done: 'Успешно', failed: 'Ошибка', running: 'Выполняется', cancelled: 'Отменён', pending: 'В очереди',
};

// --- Понятный конструктор расписания (вместо ввода cron-строки руками) ---

type ScheduleFreq = 'daily' | 'weekly' | 'hourly' | 'advanced';

const FREQ_OPTIONS: { value: ScheduleFreq; label: string }[] = [
  { value: 'daily', label: 'Каждый день' },
  { value: 'weekly', label: 'По дням недели' },
  { value: 'hourly', label: 'Каждые N часов' },
  { value: 'advanced', label: 'Свой cron (для опытных)' },
];

const WEEKDAY_OPTIONS: { cron: number; label: string }[] = [
  { cron: 1, label: 'Пн' }, { cron: 2, label: 'Вт' }, { cron: 3, label: 'Ср' }, { cron: 4, label: 'Чт' },
  { cron: 5, label: 'Пт' }, { cron: 6, label: 'Сб' }, { cron: 0, label: 'Вс' },
];
const WEEKDAY_SORT_KEY: Record<number, number> = { 1: 0, 2: 1, 3: 2, 4: 3, 5: 4, 6: 5, 0: 6 };

const TIMEZONE_OPTIONS: { value: string; label: string }[] = [
  { value: 'Europe/Moscow', label: 'Москва (UTC+3)' },
  { value: 'Europe/Kaliningrad', label: 'Калининград (UTC+2)' },
  { value: 'Europe/Samara', label: 'Самара (UTC+4)' },
  { value: 'Asia/Yekaterinburg', label: 'Екатеринбург (UTC+5)' },
  { value: 'Asia/Omsk', label: 'Омск (UTC+6)' },
  { value: 'Asia/Krasnoyarsk', label: 'Красноярск (UTC+7)' },
  { value: 'Asia/Irkutsk', label: 'Иркутск (UTC+8)' },
  { value: 'Asia/Yakutsk', label: 'Якутск (UTC+9)' },
  { value: 'Asia/Vladivostok', label: 'Владивосток (UTC+10)' },
  { value: 'Asia/Magadan', label: 'Магадан (UTC+11)' },
  { value: 'Asia/Kamchatka', label: 'Камчатка (UTC+12)' },
  { value: 'Europe/Minsk', label: 'Минск (UTC+3)' },
  { value: 'Europe/Kyiv', label: 'Киев' },
  { value: 'Asia/Almaty', label: 'Алматы (UTC+6)' },
  { value: 'Asia/Tashkent', label: 'Ташкент (UTC+5)' },
  { value: 'Asia/Baku', label: 'Баку (UTC+4)' },
  { value: 'Asia/Yerevan', label: 'Ереван (UTC+4)' },
  { value: 'Asia/Tbilisi', label: 'Тбилиси (UTC+4)' },
  { value: 'Asia/Bishkek', label: 'Бишкек (UTC+6)' },
  { value: 'Asia/Dushanbe', label: 'Душанбе (UTC+5)' },
  { value: 'Europe/London', label: 'Лондон' },
  { value: 'Europe/Berlin', label: 'Берлин' },
  { value: 'Europe/Istanbul', label: 'Стамбул (UTC+3)' },
  { value: 'Asia/Dubai', label: 'Дубай (UTC+4)' },
  { value: 'Asia/Shanghai', label: 'Шанхай (UTC+8)' },
  { value: 'Asia/Tokyo', label: 'Токио (UTC+9)' },
  { value: 'America/New_York', label: 'Нью-Йорк' },
  { value: 'UTC', label: 'UTC' },
];

function pad2(n: number): string { return String(n).padStart(2, '0'); }

function cronFromFriendly(freq: ScheduleFreq, time: string, weekdays: number[], everyHours: string): string {
  if (freq === 'hourly') {
    const n = Math.min(23, Math.max(1, parseInt(everyHours, 10) || 1));
    return n <= 1 ? '0 * * * *' : '0 */' + n + ' * * *';
  }
  const m = /^(\d{1,2}):(\d{1,2})$/.exec(time.trim());
  const hh = m ? Math.min(23, Math.max(0, parseInt(m[1], 10))) : 9;
  const mm = m ? Math.min(59, Math.max(0, parseInt(m[2], 10))) : 0;
  if (freq === 'weekly') {
    const days = weekdays.length ? [...weekdays].sort((a, b) => WEEKDAY_SORT_KEY[a] - WEEKDAY_SORT_KEY[b]).join(',') : '*';
    return mm + ' ' + hh + ' * * ' + days;
  }
  return mm + ' ' + hh + ' * * *';
}

function friendlyFromCron(cron?: string | null): { freq: ScheduleFreq; time: string; weekdays: number[]; everyHours: string } | null {
  const c = String(cron || '').trim();
  let m = /^(\d{1,2})\s+(\d{1,2})\s+\*\s+\*\s+\*$/.exec(c);
  if (m) return { freq: 'daily', time: pad2(Number(m[2])) + ':' + pad2(Number(m[1])), weekdays: [], everyHours: '6' };
  m = /^(\d{1,2})\s+(\d{1,2})\s+\*\s+\*\s+([0-6](?:,[0-6])*)$/.exec(c);
  if (m) return { freq: 'weekly', time: pad2(Number(m[2])) + ':' + pad2(Number(m[1])), weekdays: m[3].split(',').map(Number), everyHours: '6' };
  m = /^0\s+\*\/(\d{1,2})\s+\*\s+\*\s+\*$/.exec(c);
  if (m) return { freq: 'hourly', time: '09:00', weekdays: [], everyHours: String(Number(m[1])) };
  if (/^0\s+\*\s+\*\s+\*\s+\*$/.test(c)) return { freq: 'hourly', time: '09:00', weekdays: [], everyHours: '1' };
  return null;
}

function humanizeCron(cron?: string | null, tz?: string | null): string {
  if (!cron) return 'Запуск по событию';
  const tzLabel = tz ? ' (' + tz + ')' : '';
  const parsed = friendlyFromCron(cron);
  if (!parsed) return 'Cron: ' + cron + tzLabel;
  if (parsed.freq === 'daily') return 'Каждый день в ' + parsed.time + tzLabel;
  if (parsed.freq === 'weekly') {
    const labels = WEEKDAY_OPTIONS.filter((d) => parsed.weekdays.includes(d.cron)).map((d) => d.label);
    const days = labels.length ? labels.join(', ') : 'каждый день';
    return 'По ' + days + ' в ' + parsed.time + tzLabel;
  }
  return 'Каждые ' + parsed.everyHours + ' ч.' + tzLabel;
}

export function AutomationDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const isNew = !id || id === 'new';
  const [item, setItem] = useState<PipelineInstance | null>(null);
  const [runs, setRuns] = useState<PipelineRun[]>([]);
  const [graph, setGraph] = useState<AutomationGraph | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testMsg, setTestMsg] = useState<string | null>(null);
  const [advanced, setAdvanced] = useState(false);
  const [editing, setEditing] = useState(isNew);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [eName, setEName] = useState('');
  const [eDesc, setEDesc] = useState('');
  const [eCron, setECron] = useState('');
  const [eFreq, setEFreq] = useState<ScheduleFreq>('daily');
  const [eTime, setETime] = useState('09:00');
  const [eWeekdays, setEWeekdays] = useState<number[]>([]);
  const [eEveryHours, setEEveryHours] = useState('6');
  const [eTzMode, setETzMode] = useState('Europe/Moscow');
  const [eTzText, setETzText] = useState('');
  const [eWebhookUrl, setEWebhookUrl] = useState('');
  const [eTriggerType, setETriggerType] = useState<'inbound_webhook' | 'schedule'>('inbound_webhook');
  const [eAgentId, setEAgentId] = useState('');
  const [eResponseTarget, setEResponseTarget] = useState<PipelineResponseTarget>('telegram');
  const [eExtConvKey, setEExtConvKey] = useState('');
  const [eMessageField, setEMessageField] = useState('message');
  const [eReplyUrlField, setEReplyUrlField] = useState('reply_url');
  const [eChainTo, setEChainTo] = useState('');
  const [eChain, setEChain] = useState<string[]>([]);
  const [allPipelines, setAllPipelines] = useState<PipelineInstance[]>([]);
  const [webhookBusy, setWebhookBusy] = useState(false);
  const [confirmRegenToken, setConfirmRegenToken] = useState(false);
  const [copiedWebhookUrl, setCopiedWebhookUrl] = useState(false);

  const [assistantOpen, setAssistantOpen] = useState(false);
  const [aMessages, setAMessages] = useState<AutomationAssistantMessage[]>([]);
  const [aInput, setAInput] = useState('');
  const [aSending, setASending] = useState(false);
  const [aProposed, setAProposed] = useState<{ nodes: AutomationGraphNode[] } | null>(null);
  const [aProposedChain, setAProposedChain] = useState<string[] | null>(null);
  const [aApplying, setAApplying] = useState(false);
  const [aError, setAError] = useState<string | null>(null);

  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versions, setVersions] = useState<PipelineVersion[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [confirmRestoreVersion, setConfirmRestoreVersion] = useState<PipelineVersion | null>(null);

  useEffect(() => { agentsApi.list().then((r) => setAgents(r || [])).catch(() => {}); }, []);
  useEffect(() => { pipelinesApi.list().then((r) => setAllPipelines(r || [])).catch(() => {}); }, []);

  const reloadRuns = (pid: string) => { pipelinesApi.runs(pid).then((r) => setRuns(r || [])).catch(() => {}); };

  const toggleWeekday = (d: number) => {
    haptic('light');
    setEWeekdays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort((a, b) => WEEKDAY_SORT_KEY[a] - WEEKDAY_SORT_KEY[b])));
  };

  useEffect(() => {
    if (isNew || !id) return;
    Promise.all([
      pipelinesApi.list().then((all) => (all || []).find((p) => p.id === id) ?? null),
      pipelinesApi.runs(id).catch(() => [] as PipelineRun[]),
      automationGraphApi.get(id).catch(() => null),
    ])
      .then(([data, runData, graphData]) => {
        setItem(data); setRuns(runData || []); setGraph(graphData);
        if (data) {
          setEName(data.name); setEDesc(data.description || ''); setEWebhookUrl(data.response_webhook_url || '');
          setETriggerType((data.trigger_type as 'inbound_webhook' | 'schedule') || 'inbound_webhook');
          setEAgentId(data.agent_id || '');
          setEChain(Array.isArray(data.agent_chain) && data.agent_chain.length ? data.agent_chain : (data.agent_id ? [data.agent_id] : []));
          setEResponseTarget(data.response_target || 'telegram');
          setEExtConvKey(data.external_conversation_key || ''); setEMessageField(data.message_field || 'message'); setEReplyUrlField(data.reply_url_field || 'reply_url');
          setEChainTo((data.config && (data.config as any).on_complete_pipeline_id) || '');
          setECron(data.schedule_cron || '');
          const parsed = friendlyFromCron(data.schedule_cron);
          if (parsed) { setEFreq(parsed.freq); setETime(parsed.time); setEWeekdays(parsed.weekdays); setEEveryHours(parsed.everyHours); }
          else if (data.schedule_cron) setEFreq('advanced');
          const tz = data.schedule_timezone || 'Europe/Moscow';
          if (TIMEZONE_OPTIONS.some((t) => t.value === tz)) { setETzMode(tz); } else { setETzMode('custom'); setETzText(tz); }
        }
        setLoading(false);
      })
      .catch(() => { setError(true); setLoading(false); });
  }, [id, isNew]);

  const toggleEnabled = async () => {
    if (!item) return; haptic('medium'); setToggling(true);
    try { setItem(await pipelinesApi.update(item.id, { enabled: !item.enabled })); }
    catch {} finally { setToggling(false); }
  };
  const doDelete = async () => {
    if (!item) return; haptic('heavy'); setDeleting(true);
    try { await pipelinesApi.remove(item.id); nav(-1 as any); } catch { setDeleting(false); }
  };
  const test = async () => {
    if (!item) return; haptic('medium'); setTesting(true); setTestMsg(null);
    try { await pipelinesApi.test(item.id); setTestMsg('Тестовый запуск отправлен'); setTimeout(() => reloadRuns(item.id), 1200); }
    catch { setTestMsg('Не удалось запустить тест'); } finally { setTesting(false); }
  };

  const copyWebhookUrl = () => {
    if (!item?.trigger_url) return;
    haptic('light');
    if (navigator.clipboard) navigator.clipboard.writeText(item.trigger_url).then(() => { setCopiedWebhookUrl(true); setTimeout(() => setCopiedWebhookUrl(false), 1400); }).catch(() => {});
  };
  const generateWebhookUrl = async () => {
    if (!item) return;
    haptic('medium'); setWebhookBusy(true);
    try { setItem(await pipelinesApi.regenerateToken(item.id)); }
    catch {} finally { setWebhookBusy(false); setConfirmRegenToken(false); }
  };
  const resolvedTz = eTzMode === 'custom' ? (eTzText.trim() || 'Europe/Moscow') : eTzMode;
  const resolvedCron = eFreq === 'advanced' ? (eCron.trim() || null) : cronFromFriendly(eFreq, eTime, eWeekdays, eEveryHours);

  const saveEdit = async () => {
    if (!eName.trim()) return; haptic('medium'); setSaving(true);
    try {
      if (isNew) {
        const createData: PipelineCreateInput = {
          agentId: (eChain[0] ?? eAgentId) || '', name: eName.trim(), description: eDesc.trim() || null,
          responseTarget: eResponseTarget, responseWebhookUrl: eWebhookUrl || null,
          externalConversationKey: eExtConvKey || '', messageField: eMessageField || 'message', replyUrlField: eReplyUrlField || 'reply_url',
          triggerType: eTriggerType, scheduleCron: eTriggerType === 'schedule' ? resolvedCron : null, scheduleTimezone: resolvedTz,
          config: eChainTo ? { on_complete_pipeline_id: eChainTo } : undefined,
        };
        const created = await pipelinesApi.create(createData); haptic('heavy');
        nav('/automation/' + created.id, { replace: true });
      } else if (item) {
        const patch: PipelinePatchInput = {
          name: eName.trim(), description: eDesc.trim() || null, scheduleCron: eTriggerType === 'schedule' ? resolvedCron : null, scheduleTimezone: resolvedTz,
          responseWebhookUrl: eWebhookUrl || null, agentId: (eChain[0] ?? eAgentId) || null, agentChain: eChain, responseTarget: eResponseTarget,
          triggerType: eTriggerType, externalConversationKey: eExtConvKey, messageField: eMessageField, replyUrlField: eReplyUrlField,
          config: { ...(item.config || {}), on_complete_pipeline_id: eChainTo || undefined },
        };
        const updated = await pipelinesApi.update(item.id, patch); setItem(updated); setEditing(false); haptic('heavy');
      }
    } catch {} finally { setSaving(false); }
  };


  const openAssistant = () => {
    haptic('light');
    setAssistantOpen(true);
    setAError(null);
    if (aMessages.length === 0) {
      setAMessages([{ role: 'assistant', content: 'Спросите меня, почему автоматизация не сработала, или попросите поменять канал, промпт, инструменты или расписание.' }]);
    }
  };

  const sendAssistant = async () => {
    if (!item || !aInput.trim() || aSending) return;
    haptic('light');
    const userMsg: AutomationAssistantMessage = { role: 'user', content: aInput.trim() };
    const history = aMessages;
    // Если есть неподтверждённое предложение — не теряем его молча, а явно предупреждаем в чате.
    const hadPendingProposal = !!(aProposed || (aProposedChain && aProposedChain.length));
    setAMessages((prev) => [...prev, userMsg]);
    setAInput('');
    setASending(true);
    setAError(null);
    try {
      const resp = await automationAssistantApi.chat(item.id, userMsg.content, history);
      setAMessages((prev) => [
        ...prev,
        ...(hadPendingProposal ? [{ role: 'assistant' as const, content: 'Предыдущее предложение изменений снято, так как вы задали новый вопрос.' }] : []),
        { role: 'assistant', content: resp.reply },
      ]);
      setAProposed(resp.proposedGraph);
      setAProposedChain(resp.proposedAgentChain ?? null);
    } catch (err) {
      // Глухое «не удалось» не давало понять, что случилось: таймаут, сеть или ошибка сервера.
      const raw = err instanceof Error ? err.message : String(err);
      const friendly = /abort/i.test(raw) || /timeout/i.test(raw)
        ? 'Ассистент не ответил за 2 минуты. Попробуйте ещё раз или сформулируйте запрос короче.'
        : /failed to fetch|networkerror|load failed/i.test(raw)
          ? 'Нет связи с сервером. Проверьте интернет и повторите запрос.'
          : `Не удалось получить ответ ассистента: ${raw.slice(0, 200)}`;
      setAError(friendly);
    } finally {
      setASending(false);
    }
  };

  // Единая точка применения: раньше граф и цепочка агентов применялись двумя независимыми
  // кнопками подтверждения, и одно могло было затереть другим — теперь одна кнопка,
  // которая применяет сначала цепочку, потом граф (если предложены оба вместе).
  const applyProposedAll = async () => {
    const hasChain = !!(aProposedChain && aProposedChain.length);
    if (!item || (!aProposed && !hasChain)) return;
    haptic('medium'); setAApplying(true); setAError(null);
    try {
      let workingId = item.id;
      if (hasChain && aProposedChain) {
        const updated = await pipelinesApi.update(item.id, { agentChain: aProposedChain });
        setItem(updated);
        setEChain(Array.isArray(updated.agent_chain) && updated.agent_chain.length ? updated.agent_chain : (updated.agent_id ? [updated.agent_id] : []));
        workingId = updated.id;
      }
      if (aProposed) {
        const updatedGraph = await automationGraphApi.save(workingId, aProposed.nodes);
        setGraph(updatedGraph);
      } else {
        const g = await automationGraphApi.get(workingId).catch(() => null);
        if (g) setGraph(g);
      }
      const all = await pipelinesApi.list().catch(() => [] as PipelineInstance[]);
      const fresh = (all || []).find((pp) => pp.id === workingId) || null;
      if (fresh) setItem(fresh);
      setAProposed(null);
      setAProposedChain(null);
      setAMessages((prev) => [...prev, { role: 'assistant', content: 'Готово, изменения применены. Предыдущее состояние сохранено в истории версий — можно откатиться.' }]);
      haptic('heavy');
    } catch {
      setAError('Не удалось применить изменения');
    } finally {
      setAApplying(false);
    }
  };

  const openVersions = () => {
    if (!item) return;
    haptic('light');
    setVersionsOpen(true);
    setVersionsError(null);
    setVersionsLoading(true);
    pipelinesApi.versions(item.id).then((r) => setVersions(r || [])).catch(() => setVersionsError('Не удалось загрузить историю версий')).finally(() => setVersionsLoading(false));
  };

  const restoreVersion = async (v: PipelineVersion) => {
    if (!item) return;
    haptic('medium'); setRestoringId(v.id); setVersionsError(null);
    try {
      const restored = await pipelinesApi.restoreVersion(item.id, v.id);
      setItem(restored);
      setEChain(Array.isArray(restored.agent_chain) && restored.agent_chain.length ? restored.agent_chain : (restored.agent_id ? [restored.agent_id] : []));
      const g = await automationGraphApi.get(item.id).catch(() => null);
      if (g) setGraph(g);
      setConfirmRestoreVersion(null);
      setVersionsOpen(false);
      haptic('heavy');
    } catch {
      setVersionsError('Не удалось восстановить версию');
    } finally {
      setRestoringId(null);
    }
  };

  const Back = (
    <div className="rf-topbar">
      <button className="rf-back" onClick={() => { haptic('light'); nav(-1 as any); }}><ArrowLeft />Назад</button>
    </div>
  );

  if (loading) return <div className="rf-detail">{Back}<div className="rf-loading"><div className="rf-spin" /></div></div>;

  if (!isNew && (error || !item)) return (
    <div className="rf-detail">{Back}
      <div className="rf-empty"><div className="rf-empty-ic"><AlertCircle /></div><h3>Сценарий не найден</h3><p>Возможно, он был удалён</p></div>
    </div>
  );

  if (isNew || editing) {
    return (
      <div className="rf-detail">
        <div className="rf-topbar">
          <button className="rf-back" onClick={() => { haptic('light'); if (isNew) nav(-1 as any); else setEditing(false); }}><ArrowLeft />Назад</button>
          <div className="rf-topbar-title">{isNew ? 'Новый сценарий' : 'Редактирование'}</div>
        </div>
        <div className="rf-detail-scroll">
          <div className="rf-field"><label>Название</label>
            <input className="rf-input" value={eName} onChange={(e) => setEName(e.target.value)} placeholder="Например: Утренний отчёт" />
          </div>
          <div className="rf-field"><label>Описание</label>
            <textarea className="rf-textarea" value={eDesc} onChange={(e) => setEDesc(e.target.value)} placeholder="Что делает этот сценарий" />
          </div>
          <div className="rf-field"><label>Агенты сценария (выполняются по очереди)</label>
            <div className="rf-hint rf-mb8">Выход каждого агента передаётся на вход следующему. Пусто — будет использован Оркестратор по умолчанию.</div>
            {eChain.map((aid, i) => (
              <div key={i} className="rf-chain-row">
                <span className="rf-chain-idx">{i + 1}</span>
                <select className="rf-select" value={aid} onChange={(e) => { const v = e.target.value; setEChain((c) => c.map((x, j) => (j === i ? v : x))); }}>
                  {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                <button type="button" className="rf-btn ghost icon" title="Вверх" disabled={i === 0} onClick={() => setEChain((c) => { const n = [...c]; const t = n[i - 1]; n[i - 1] = n[i]; n[i] = t; return n; })}>↑</button>
                <button type="button" className="rf-btn ghost icon" title="Вниз" disabled={i === eChain.length - 1} onClick={() => setEChain((c) => { const n = [...c]; const t = n[i + 1]; n[i + 1] = n[i]; n[i] = t; return n; })}>↓</button>
                <button type="button" className="rf-btn ghost icon" title="Убрать" onClick={() => setEChain((c) => c.filter((_, j) => j !== i))}><IconX /></button>
              </div>
            ))}
            {agents.length > 0 && (
              <button type="button" className="rf-btn ghost" onClick={() => { haptic('light'); setEChain((c) => [...c, agents.find((a) => !c.includes(a.id))?.id || agents[0].id]); }}><IconPlus />Добавить агента</button>
            )}
          </div>
          <div className="rf-field"><label>Тип триггера</label>
            <select className="rf-select" value={eTriggerType} onChange={(e) => setETriggerType(e.target.value as 'inbound_webhook' | 'schedule')}>
              {TRIGGER_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          {!isNew && item && (
            <div className="rf-field"><label>Ссылка для запуска извне</label>
              {item.trigger_url ? (
                <>
                  <div className="rf-tokenrow"><code className="rf-token">{item.trigger_url}</code><button type="button" className="rf-btn sm icon" title="Скопировать" onClick={copyWebhookUrl}>{copiedWebhookUrl ? <Check /> : <Copy />}</button></div>
                  <div className="rf-hint">Эта ссылка запускает сценарий извне: отправьте на неё запрос из другого сервиса, скрипта или внешнего конструктора автоматизации (например, n8n или Zapier) — и сценарий выполнится. Работает независимо от выбранного триггера выше — можно одновременно запускать сценарий и по расписанию, и этой ссылкой. Ссылка не одноразовая — её всегда можно посмотреть и скопировать здесь.</div>
                  <button type="button" className="rf-btn ghost rf-mt10" onClick={() => setConfirmRegenToken(true)} disabled={webhookBusy}>{webhookBusy ? <Loader2 className="spin" /> : null}Перевыпустить ссылку</button>
                </>
              ) : (
                <>
                  <div className="rf-hint">Можно выдать ссылку, чтобы запускать этот сценарий извне — в дополнение к выбранному триггеру выше.</div>
                  <button type="button" className="rf-btn" onClick={generateWebhookUrl} disabled={webhookBusy}>{webhookBusy ? <Loader2 className="spin" /> : <IconPlus />}Сгенерировать ссылку</button>
                </>
              )}
            </div>
          )}
          {eTriggerType === 'schedule' && (
            <>
              <div className="rf-field"><label>Как часто запускать</label>
                <select className="rf-select" value={eFreq} onChange={(e) => setEFreq(e.target.value as ScheduleFreq)}>
                  {FREQ_OPTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </div>

              {(eFreq === 'daily' || eFreq === 'weekly') && (
                <div className="rf-field"><label>Время запуска</label>
                  <input className="rf-input" type="time" value={eTime} onChange={(e) => setETime(e.target.value)} />
                </div>
              )}

              {eFreq === 'weekly' && (
                <div className="rf-field"><label>Дни недели</label>
                  <div className="rf-chipgrid">
                    {WEEKDAY_OPTIONS.map((d) => (
                      <button type="button" key={d.cron} className={'rf-chiptog' + (eWeekdays.includes(d.cron) ? ' on' : '')} onClick={() => toggleWeekday(d.cron)}>{eWeekdays.includes(d.cron) ? <Check /> : null}{d.label}</button>
                    ))}
                  </div>
                  <div className="rf-hint">Если не выбрать ни одного дня — запуск будет каждый день</div>
                </div>
              )}

              {eFreq === 'hourly' && (
                <div className="rf-field"><label>Повторять каждые сколько часов</label>
                  <input className="rf-input" type="number" min={1} max={23} value={eEveryHours} onChange={(e) => setEEveryHours(e.target.value)} placeholder="6" />
                </div>
              )}

              {eFreq === 'advanced' && (
                <div className="rf-field"><label>Cron-расписание</label>
                  <input className="rf-input" value={eCron} onChange={(e) => setECron(e.target.value)} placeholder="0 9 * * *" />
                  <div className="rf-hint">Формат: минуты часы день месяц день_недели</div>
                </div>
              )}

              {eFreq !== 'advanced' && (
                <div className="rf-hint">Расписание (cron): {cronFromFriendly(eFreq, eTime, eWeekdays, eEveryHours)}</div>
              )}

              <div className="rf-field"><label>Часовой пояс</label>
                <select className="rf-select" value={eTzMode} onChange={(e) => setETzMode(e.target.value)}>
                  {TIMEZONE_OPTIONS.map((tz) => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
                  <option value="custom">Другой (ввести вручную)</option>
                </select>
                {eTzMode === 'custom' && (
                  <input className="rf-input rf-mt10" value={eTzText} onChange={(e) => setETzText(e.target.value)} placeholder="Europe/Moscow" />
                )}
              </div>
            </>
          )}
          <div className="rf-field"><label>Куда отправлять результат</label>
            <select className="rf-select" value={eResponseTarget} onChange={(e) => setEResponseTarget(e.target.value as PipelineResponseTarget)}>
              {RESPONSE_TARGETS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          {(eResponseTarget === 'outbound_webhook' || eResponseTarget === 'both') && (
            <div className="rf-field"><label>URL доставки (вебхук)</label>
              <input className="rf-input" value={eWebhookUrl} onChange={(e) => setEWebhookUrl(e.target.value)} placeholder="https://..." />
            </div>
          )}
          <div className="rf-field"><label>По завершении запустить автоматизацию</label>
            <select className="rf-select" value={eChainTo} onChange={(e) => setEChainTo(e.target.value)}>
              <option value="">— Ничего (не перехватывать) —</option>
              {allPipelines.filter((pp) => pp.id !== id).map((pp) => <option key={pp.id} value={pp.id}>{pp.name}</option>)}
            </select>
            <div className="rf-hint">Результат этого сценария станет входным сообщением выбранной автоматизации</div>
          </div>
          <button className="rf-btn ghost block rf-mb8" onClick={() => setAdvanced(!advanced)}>
            <ChevronDown />Дополнительные параметры
          </button>
          {advanced && (
            <>
              <div className="rf-field"><label>External conversation key</label>
                <input className="rf-input" value={eExtConvKey} onChange={(e) => setEExtConvKey(e.target.value)} placeholder="tg:user_id" />
                <div className="rf-hint">Ключ для маршрутизации сообщений</div>
              </div>
              <div className="rf-field"><label>Поле сообщения</label>
                <input className="rf-input" value={eMessageField} onChange={(e) => setEMessageField(e.target.value)} placeholder="message" />
              </div>
              <div className="rf-field"><label>Поле URL ответа</label>
                <input className="rf-input" value={eReplyUrlField} onChange={(e) => setEReplyUrlField(e.target.value)} placeholder="reply_url" />
              </div>
            </>
          )}
          <div className="rf-btn-row">
            <button className="rf-btn primary" onClick={saveEdit} disabled={saving || !eName.trim()}>{saving ? <Loader2 className="spin" /> : <IconSave />}{isNew ? 'Создать' : 'Сохранить'}</button>
            <button className="rf-btn" onClick={() => { if (isNew) nav(-1 as any); else setEditing(false); }}><X />Отмена</button>
          </div>
        </div>
        <ConfirmSheet
          open={confirmRegenToken}
          title="Перевыпустить ссылку?"
          busy={webhookBusy}
          onConfirm={generateWebhookUrl}
          onCancel={() => { if (!webhookBusy) setConfirmRegenToken(false); }}
        >
          Старая ссылка перестанет работать. Если она уже где-то используется (в другом сервисе или скрипте), замените её на новую после перевыпуска.
        </ConfirmSheet>
      </div>
    );
  }

  const p = item!;
  const triggerLabel = p.trigger_type === 'schedule' ? 'Расписание' : p.trigger_type === 'inbound_webhook' ? 'Вебхук' : p.trigger_type || 'Триггер';
  const midNodes = graph?.nodes?.filter((n) => n.kind !== 'trigger' && n.kind !== 'response') ?? [];
  const agentName = agents.find((a) => a.id === p.agent_id)?.name || p.agent_id;
  const KIND_LABEL: Record<string, string> = { agent: 'АГЕНТ', tool: 'ИНСТРУМЕНТ' };
  const midDisplay = (midNodes.length > 0
    ? midNodes
    : (p.agent_id ? [{ id: 'agent', kind: 'agent', label: String(agentName ?? '') }] : [])) as Array<{ id: string; kind: string; label: string }>;
  const chainId = (p.config && (p.config as any).on_complete_pipeline_id) as string | undefined;
  const services = ((p.config && (p.config as any).services) || []) as Array<{ id: string; name: string }>;
  const chainTarget = chainId ? (allPipelines.find((x) => x.id === chainId) || ({ name: 'Автоматизация ' + chainId.slice(0, 8) } as PipelineInstance)) : null;
  // Агенты этого сценария, которые будут удалены вместе с ним (не используются больше нигде).
  const scenarioChain = (Array.isArray(p.agent_chain) && p.agent_chain.length ? p.agent_chain : (p.agent_id ? [p.agent_id] : []));
  const otherPipes = allPipelines.filter((x) => x.id !== p.id);
  const agentsToDelete = scenarioChain.filter((aid) => !otherPipes.some((x) => x.agent_id === aid || (Array.isArray(x.agent_chain) && x.agent_chain.includes(aid))));
  const agentsToDeleteNames = agentsToDelete.map((aid) => agents.find((a) => a.id === aid)?.name || aid);

  return (
    <div className="rf-detail">
      <div className="rf-topbar">
        <button className="rf-back" onClick={() => { haptic('light'); nav(-1 as any); }}><ArrowLeft />Назад</button>
        <div className="rf-topbar-title">{p.name}</div>
      </div>

      <div className="rf-detail-scroll">
        <div className="rf-card rf-dcard">
          <div className="rf-dcard-head">
            <div className="rf-dcard-art"><Zap /></div>
            <div className="rf-dcard-info">
              <div className="rf-dcard-title">{p.name}</div>
              <div className="rf-dcard-desc">{p.description || 'Многоуровневый сценарий автоматизации'}</div>
              <div className="rf-mt10">
                <span className={'rf-pill ' + (p.enabled ? 'on' : '')}><span className="dot" />{p.admin_paused ? '⏸ Приостановлена администрацией' : p.enabled ? 'Активен' : 'Выключен'}</span>
              </div>
            </div>
          </div>
          <div className="rf-btn-row">
            <button className="rf-btn primary" onClick={toggleEnabled} disabled={toggling}>{toggling ? <Loader2 className="spin" /> : <Power />}{p.enabled ? 'Выключить' : 'Включить'}</button>
            <button className="rf-btn" onClick={test} disabled={testing}>{testing ? <Loader2 className="spin" /> : <Play />}Тест</button>
            <button className="rf-btn" onClick={() => { haptic('light'); setEditing(true); }}><Edit3 />Изменить</button>
            <button className="rf-btn danger icon" onClick={() => { haptic('light'); setConfirmDel(true); }}><Trash2 /></button>
          </div>
          {testMsg && <div className="rf-hint rf-mt10">{testMsg}</div>}
          <button className="rf-btn ghost block rf-mt10" onClick={openAssistant}><IconChat />Спросить оркестратора</button>
          <button className="rf-btn ghost block rf-mt10" onClick={openVersions}><History />История версий</button>
        </div>

        <div className="rf-seclabel">Как работает</div>
        <div className="rf-card rf-cardpad">
          <div className="rf-timeline">
            <div className="rf-tl">
              <div className="rf-tl-rail"><div className="rf-tl-dot accent"><Zap /></div><div className="rf-tl-line" /></div>
              <div className="rf-tl-body">
                <div className="rf-tl-label">ТРИГГЕР</div>
                <div className="rf-tl-title">{triggerLabel}</div>
                <div className="rf-tl-desc">{p.schedule_cron ? humanizeCron(p.schedule_cron, p.schedule_timezone) : 'Запуск по событию'}</div>
              </div>
            </div>
            {midDisplay.map((n) => (
              <div className="rf-tl" key={n.id}>
                <div className="rf-tl-rail"><div className="rf-tl-dot">{n.kind === 'agent' ? <Bot /> : <Settings2 />}</div><div className="rf-tl-line" /></div>
                <div className="rf-tl-body"><div className="rf-tl-label">{KIND_LABEL[n.kind] || n.kind.toUpperCase()}</div><div className="rf-tl-title">{n.label}</div></div>
              </div>
            ))}
            {services.length > 0 && (
              <div className="rf-tl">
                <div className="rf-tl-rail"><div className="rf-tl-dot"><Plug /></div><div className="rf-tl-line" /></div>
                <div className="rf-tl-body"><div className="rf-tl-label">СЕРВИСЫ</div><div className="rf-tl-title">{services.map((sv) => sv.name).join(', ')}</div><div className="rf-tl-desc">Подключённые интеграции</div></div>
              </div>
            )}
            <div className="rf-tl">
              <div className="rf-tl-rail"><div className="rf-tl-dot"><Webhook /></div>{chainTarget && <div className="rf-tl-line" />}</div>
              <div className="rf-tl-body">
                <div className="rf-tl-label">РЕЗУЛЬТАТ</div>
                <div className="rf-tl-title">{RESP_LABEL[p.response_target] || p.response_target}</div>
                <div className="rf-tl-desc">{p.response_webhook_url ? 'URL: ' + p.response_webhook_url : 'Доставка по умолчанию'}</div>
              </div>
            </div>
            {chainTarget && (
              <div className="rf-tl">
                <div className="rf-tl-rail"><div className="rf-tl-dot accent"><Zap /></div></div>
                <div className="rf-tl-body">
                  <div className="rf-tl-label">ПО ЗАВЕРШЕНИИ</div>
                  <div className="rf-tl-title">{chainTarget.name}</div>
                  <div className="rf-tl-desc">Автоматически запускается следующей</div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="rf-seclabel">История запусков</div>
        {runs.length === 0 ? (
          <div className="rf-empty"><div className="rf-empty-ic"><Clock /></div><h3>Нет запусков</h3><p>Сценарий ещё не запускался</p></div>
        ) : (
          <div>
            {runs.slice(0, 20).map((run) => (
              <div key={run.id} className="rf-card rf-run">
                <div className={'rf-run-dot ' + run.status} />
                <div className="rf-run-body">
                  <div className="rf-run-status">{RUN_LABEL[run.status] || run.status}</div>
                  <div className="rf-run-time">{new Date(run.created_at).toLocaleString('ru-RU')}</div>
                  {run.error && <div className="rf-run-time rf-err">{run.error}</div>}
                </div>
              </div>
            ))}
          </div>
        )}

        <button className="rf-btn ghost block rf-mt18" onClick={() => { haptic('light'); setAdvanced(!advanced); }}>
          <ChevronDown className={advanced ? 'rf-chev-up' : ''} />Технические детали
        </button>
        {advanced && (
          <div className="rf-card rf-kvcard">
            <div className="rf-hint rf-mb8">Служебные данные для отладки и интеграций — как сценарий запускается и какими ключами связаны сообщения. Здесь их нельзя редактировать; обычному пользователю они, как правило, не нужны.</div>
            <div className="rf-kv"><div className="rf-kv-k">ID</div><div className="rf-kv-v mono">{p.id}</div></div>
            <div className="rf-kv"><div className="rf-kv-k">Тип триггера</div><div className="rf-kv-v">{TRIGGER_TYPES.find((t) => t.value === p.trigger_type)?.label || p.trigger_type}</div></div>
            {p.trigger_type === 'schedule' && <div className="rf-kv"><div className="rf-kv-k">Cron</div><div className="rf-kv-v mono">{p.schedule_cron || '—'}</div></div>}
            {p.trigger_type === 'schedule' && <div className="rf-kv"><div className="rf-kv-k">Часовой пояс</div><div className="rf-kv-v mono">{p.schedule_timezone || '—'}</div></div>}
            {p.trigger_type === 'inbound_webhook' && <div className="rf-kv"><div className="rf-kv-k">Ключ разговора</div><div className="rf-kv-v mono">{p.external_conversation_key || '—'}</div></div>}
            {p.trigger_type === 'inbound_webhook' && <div className="rf-kv"><div className="rf-kv-k">Поле сообщения</div><div className="rf-kv-v mono">{p.message_field || '—'}</div></div>}
            {p.token && <div className="rf-kv"><div className="rf-kv-k">Токен</div><div className="rf-kv-v mono">{p.token}</div></div>}
            {p.trigger_url && <div className="rf-kv"><div className="rf-kv-k">URL триггера</div><div className="rf-kv-v mono">{p.trigger_url}</div></div>}
          </div>
        )}
      </div>

      {assistantOpen && (
        <div className="rf-sheet-backdrop" onClick={() => setAssistantOpen(false)}>
          <div className="rf-sheet rf-chat-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="rf-sheet-handle" />
            <div className="rf-sheet-head">
              <div className="rf-sheet-title">Ассистент автоматизации</div>
              <button className="rf-btn ghost icon" onClick={() => setAssistantOpen(false)}><X /></button>
            </div>
            <div className="rf-chat-list">
              {aMessages.map((m, i) => (
                <div key={i} className={'rf-chat-msg ' + m.role}>{m.content}</div>
              ))}
              {aSending && <div className="rf-chat-msg assistant"><Loader2 className="spin" /></div>}
              {aError && <div className="rf-hint rf-mb8">{aError}</div>}
            </div>
            {(aProposed || (aProposedChain && aProposedChain.length > 0)) && (
              <div className="rf-sheet-actions">
                <button className="rf-btn primary" onClick={applyProposedAll} disabled={aApplying}>
                  {aApplying ? <Loader2 className="spin" /> : <Check />}
                  {aProposedChain && aProposedChain.length > 0
                    ? (aProposed ? 'Применить изменения и цепочку' : `Применить цепочку (${aProposedChain.map((cid) => agents.find((a) => a.id === cid)?.name || '?').join(' → ')})`)
                    : 'Применить изменения'}
                </button>
                <button className="rf-btn" onClick={() => { setAProposed(null); setAProposedChain(null); haptic('light'); }}><X />Отклонить</button>
              </div>
            )}
            <div className="rf-sheet-actions">
              <input
                className="rf-input"
                value={aInput}
                onChange={(e) => setAInput(e.target.value)}
                placeholder="Например: почему не сработало вчера?"
                onKeyDown={(e) => { if (e.key === 'Enter') sendAssistant(); }}
              />
              <button className="rf-btn primary icon" onClick={sendAssistant} disabled={aSending || !aInput.trim()}><Send /></button>
            </div>
          </div>
        </div>
      )}
      {versionsOpen && (
        <div className="rf-sheet-backdrop" onClick={() => setVersionsOpen(false)}>
          <div className="rf-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="rf-sheet-handle" />
            <div className="rf-sheet-head">
              <div className="rf-sheet-title">История версий</div>
              <button className="rf-btn ghost icon" onClick={() => setVersionsOpen(false)}><X /></button>
            </div>
            <div className="rf-hint rf-mb8">Хранится не более 3 последних состояний, не старше 24 часов, сохраняются перед правками через ассистента или редактор схемы.</div>
            {versionsError && <div className="rf-hint rf-mb8">{versionsError}</div>}
            {versionsLoading ? (
              <div className="rf-loading"><div className="rf-spin" /></div>
            ) : versions.length === 0 ? (
              <div className="rf-hint">Пока нет сохранённых версий</div>
            ) : (
              <div>
                {versions.map((v) => (
                  <div key={v.id} className="rf-card rf-cardpad rf-mt10">
                    <div className="rf-tl-title">{v.label || 'Версия'}</div>
                    <div className="rf-hint">{new Date(v.created_at).toLocaleString('ru-RU')}</div>
                    <button className="rf-btn block rf-mt10" onClick={() => { haptic('light'); setConfirmRestoreVersion(v); }} disabled={restoringId === v.id}>{restoringId === v.id ? <Loader2 className="spin" /> : 'Восстановить'}</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      <ConfirmSheet
        open={!!confirmRestoreVersion}
        title="Восстановить эту версию?"
        busy={!!restoringId}
        onConfirm={() => confirmRestoreVersion && restoreVersion(confirmRestoreVersion)}
        onCancel={() => { if (!restoringId) setConfirmRestoreVersion(null); }}
      >
        Текущие настройки сценария и промпты агентов будут заменены сохранённой версией. Текущее состояние перед этим тоже сохранится, так что откат можно будет отменить.
      </ConfirmSheet>
      <ConfirmSheet
        open={confirmDel}
        title="Удалить сценарий?"
        busy={deleting}
        onConfirm={doDelete}
        onCancel={() => { if (!deleting) setConfirmDel(false); }}
      >
        Сценарий <b>«{p.name}»</b> будет удалён безвозвратно.
        {agentsToDeleteNames.length > 0 ? (
          <>
            <br /><br />Вместе с ним удалятся агенты этого сценария ({agentsToDeleteNames.length}):
            <ul className="rf-confirm-list">{agentsToDeleteNames.map((nm, i) => <li key={i}>{nm}</li>)}</ul>
          </>
        ) : (
          <><br /><br />Агенты этого сценария используются в других сценариях и удалены не будут.</>
        )}
      </ConfirmSheet>
    </div>
  );
}
